"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractERC20AmountFromCommitmentCiphertext = exports.extractFirstNoteERC20AmountMapFromTransactionRequest = void 0;
const engine_1 = require("@railgun-community/engine");
const engine_2 = require("../core/engine");
const wallets_1 = require("../wallets");
const shared_models_1 = require("@railgun-community/shared-models");
const providers_1 = require("../core/providers");
const utils_1 = require("../../../utils");
const util_1 = require("../util");
const error_1 = require("../../../utils/error");
const ethers_1 = require("ethers");
var TransactionName;
(function (TransactionName) {
    TransactionName["RailgunSmartWallet"] = "transact";
    TransactionName["RelayAdapt"] = "relay";
})(TransactionName || (TransactionName = {}));
const getABIForTransaction = (transactionName) => {
    switch (transactionName) {
        case TransactionName.RailgunSmartWallet:
            return engine_1.ABIRailgunSmartWallet;
        case TransactionName.RelayAdapt:
            return engine_1.ABIRelayAdapt;
    }
};
const extractFirstNoteERC20AmountMapFromTransactionRequest = (railgunWalletID, network, transactionRequest, useRelayAdapt) => {
    const transactionName = useRelayAdapt
        ? TransactionName.RelayAdapt
        : TransactionName.RailgunSmartWallet;
    const contractAddress = useRelayAdapt
        ? network.relayAdaptContract
        : network.proxyContract;
    return extractFirstNoteERC20AmountMap(railgunWalletID, network, transactionRequest, transactionName, contractAddress);
};
exports.extractFirstNoteERC20AmountMapFromTransactionRequest = extractFirstNoteERC20AmountMapFromTransactionRequest;
const recursivelyDecodeResult = (result) => {
    if (typeof result !== 'object') {
        // End (primitive) value
        return result;
    }
    try {
        const obj = result.toObject();
        if ('_' in obj) {
            throw new Error('Decode as array, not object');
        }
        Object.keys(obj).forEach(key => {
            obj[key] = recursivelyDecodeResult(obj[key]);
        });
        return obj;
    }
    catch (err) {
        // Result is array.
        return result
            .toArray()
            .map(item => recursivelyDecodeResult(item));
    }
};
const extractFirstNoteERC20AmountMap = async (railgunWalletID, network, transactionRequest, transactionName, contractAddress) => {
    const abi = getABIForTransaction(transactionName);
    if (!transactionRequest.to ||
        transactionRequest.to.toLowerCase() !== contractAddress.toLowerCase()) {
        throw new Error(`Invalid contract address: got ${transactionRequest.to}, expected ${contractAddress} for network ${network.name}`);
    }
    const provider = (0, providers_1.getFallbackProviderForNetwork)(network.name);
    const contract = new ethers_1.Contract(contractAddress, abi, provider);
    const parsedTransaction = contract.interface.parseTransaction({
        data: transactionRequest.data ?? '',
        value: transactionRequest.value,
    });
    if (!parsedTransaction) {
        throw new Error('No transaction parsable from request');
    }
    if (parsedTransaction.name !== transactionName) {
        throw new Error(`Contract method invalid: expected ${transactionName}`);
    }
    const args = recursivelyDecodeResult(parsedTransaction.args);
    const railgunWallet = (0, engine_2.walletForID)(railgunWalletID);
    const railgunWalletAddress = railgunWallet.getAddress();
    const receivingViewingPrivateKey = railgunWallet.viewingKeyPair.privateKey;
    const receivingRailgunAddressData = (0, wallets_1.getRailgunWalletAddressData)(railgunWalletAddress);
    const erc20PaymentAmounts = {};
    // eslint-disable-next-line no-underscore-dangle
    const railgunTxs = args._transactions;
    await Promise.all(railgunTxs.map(async (railgunTx) => {
        const { commitments, boundParams } = railgunTx;
        // Extract first commitment (index 0)
        const index = 0;
        const commitmentCiphertextStructOutput = boundParams.commitmentCiphertext[index];
        const commitmentHash = commitments[index];
        if (!(0, shared_models_1.isDefined)(commitmentCiphertextStructOutput)) {
            (0, utils_1.sendMessage)('no ciphertext found for commitment at index 0');
            return;
        }
        const commitmentCiphertext = (0, engine_1.formatCommitmentCiphertext)(commitmentCiphertextStructOutput);
        const erc20PaymentAmount = await (0, exports.extractERC20AmountFromCommitmentCiphertext)(network, commitmentCiphertext, commitmentHash, receivingViewingPrivateKey, receivingRailgunAddressData);
        if (!erc20PaymentAmount) {
            return;
        }
        const { tokenAddress, amount } = erc20PaymentAmount;
        if (!(0, shared_models_1.isDefined)(erc20PaymentAmounts[tokenAddress])) {
            erc20PaymentAmounts[tokenAddress] = 0n;
        }
        erc20PaymentAmounts[tokenAddress] += amount;
    }));
    return erc20PaymentAmounts;
};
const decryptReceiverNoteSafe = async (commitmentCiphertext, receivingViewingPrivateKey, receivingRailgunAddressData, chain) => {
    try {
        const blindedSenderViewingKey = (0, engine_1.hexStringToBytes)(commitmentCiphertext.blindedSenderViewingKey);
        const blindedReceiverViewingKey = (0, engine_1.hexStringToBytes)(commitmentCiphertext.blindedReceiverViewingKey);
        const sharedKey = await (0, engine_1.getSharedSymmetricKey)(receivingViewingPrivateKey, blindedSenderViewingKey);
        if (!sharedKey) {
            (0, utils_1.sendMessage)('invalid sharedKey');
            return undefined;
        }
        const { db } = (0, engine_2.getEngine)();
        const tokenDataGetter = new engine_1.TokenDataGetter(db, chain);
        const note = await engine_1.TransactNote.decrypt(receivingRailgunAddressData, commitmentCiphertext.ciphertext, sharedKey, commitmentCiphertext.memo, commitmentCiphertext.annotationData, blindedReceiverViewingKey, // blindedReceiverViewingKey
        blindedSenderViewingKey, // blindedSenderViewingKey
        undefined, // senderRandom - not used
        false, // isSentNote
        false, // isLegacyDecryption
        tokenDataGetter, undefined);
        return note;
    }
    catch (err) {
        (0, error_1.reportAndSanitizeError)(decryptReceiverNoteSafe.name, err);
        return undefined;
    }
};
const extractERC20AmountFromCommitmentCiphertext = async (network, commitmentCiphertext, commitmentHash, receivingViewingPrivateKey, receivingRailgunAddressData) => {
    const decryptedReceiverNote = await decryptReceiverNoteSafe(commitmentCiphertext, receivingViewingPrivateKey, receivingRailgunAddressData, network.chain);
    if (!decryptedReceiverNote) {
        // Addressed to us, but different note than fee.
        (0, utils_1.sendMessage)('invalid decryptedReceiverNote');
        return;
    }
    if (decryptedReceiverNote.receiverAddressData.masterPublicKey !==
        receivingRailgunAddressData.masterPublicKey) {
        (0, utils_1.sendMessage)('invalid masterPublicKey');
        return;
    }
    const noteHash = (0, engine_1.nToHex)(decryptedReceiverNote.hash, engine_1.ByteLength.UINT_256);
    const commitHash = (0, engine_1.formatToByteLength)(commitmentHash, engine_1.ByteLength.UINT_256);
    if (noteHash !== commitHash) {
        (0, utils_1.sendMessage)('invalid commitHash');
        return;
    }
    const { tokenData } = decryptedReceiverNote;
    if (tokenData.tokenType !== engine_1.TokenType.ERC20) {
        (0, utils_1.sendMessage)('not an erc20');
        return;
    }
    const tokenAddress = (0, util_1.parseRailgunTokenAddress)(tokenData.tokenAddress).toLowerCase();
    const amount = decryptedReceiverNote.value;
    const erc20Amount = {
        tokenAddress,
        amount,
    };
    return erc20Amount;
};
exports.extractERC20AmountFromCommitmentCiphertext = extractERC20AmountFromCommitmentCiphertext;
